'use client';

import {COOKIE_YM_UID, getCookie} from '@utils/cookies';
import {generateMyTrackerPixel} from '@utils/pixel';
import {useTracePathname} from '@utils/tracer/useTracePathname';
import {usePathname, useSearchParams} from 'next/navigation';
import React, {useEffect, useLayoutEffect} from 'react';

import {ym_log} from '@web-landing/components/Downloader/analytics';

import {logScrollDepth} from '@web-landing/analytics/helpers/logScrollDepth';
import {
  emitTmrEvent,
  enableTmrDebugging,
} from '@web-landing/analytics/helpers/logTmrEvent';
import {emitYmEvent} from '@web-landing/analytics/helpers/ymEvent';

import {
  extractScrollTargetAttributes,
  findAllScrollTargets,
} from '@web-landing/utils/scrollTarget';

import {YANDEX_METRIKA_ID} from '@web-landing/analytics/ids';

type Props = {
  userIp: string | null;
  cookieDomain?: string;
  children?: React.ReactNode;
  deeplinkCatalogUrl: string;
};

export const AnalyticsLayout: React.FC<Props> = ({
  userIp,
  cookieDomain,
  children,
  deeplinkCatalogUrl,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (searchParams?.get('_tmr_debug')) {
      enableTmrDebugging();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const scrollTargets = findAllScrollTargets();
    scrollTargets
      .map((item) => {
        return {
          height: window.scrollY + item.getBoundingClientRect().top,
          item,
        };
      })
      .sort((a, b) => a.height - b.height)
      .forEach(({item}, index) => {
        item.setAttribute('data-scroll-target-depth', (index + 1).toString());
      });
  }, [pathname]);

  useEffect(() => {
    generateMyTrackerPixel({
      cookieDomain: cookieDomain || '',
      userIp: userIp ?? '',
      appDeeplinkBaseUrl: deeplinkCatalogUrl,
      serviceVersion: `${process.env.APP_VERSION}`,
      emitPixelEvents: (payload) => {
        emitTmrEvent({
          ...payload,
          metrikaUid: getCookie(COOKIE_YM_UID) ?? '',
          pageURL: document.location.href.split('?')[0],
        });
        emitYmEvent('mt_pixel', payload);
      },
    });
  }, [cookieDomain, deeplinkCatalogUrl, pathname, searchParams, userIp]);

  useEffect(() => {
    const scrollTargets = findAllScrollTargets();

    if (!scrollTargets.length) {
      return;
    }

    const targetsHit = new Set();

    const cleanupCallbacks = scrollTargets.map((target) => {
      const attributes = extractScrollTargetAttributes(target);

      if (!attributes) {
        return;
      }

      const {eventName, scrollDepth, threshold} = attributes;

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !targetsHit.has(scrollDepth)) {
            targetsHit.add(scrollDepth);

            logScrollDepth(eventName, scrollDepth);
          }
        },
        {threshold},
      );

      const getIntersectionTarget = () => target.firstElementChild ?? target;
      let currentIntersectionTarget = getIntersectionTarget();
      intersectionObserver.observe(currentIntersectionTarget);

      /**
       * this is necessary to observe correctly in case scroll target has children that may render after initial render
       * (e.g. after omicron config fetched)
       */
      const mutationObserver = new MutationObserver(() => {
        const newIntersectionTarget = getIntersectionTarget();

        if (currentIntersectionTarget !== newIntersectionTarget) {
          intersectionObserver.unobserve(currentIntersectionTarget);
          intersectionObserver.observe(newIntersectionTarget);
          currentIntersectionTarget = newIntersectionTarget;
        }
      });

      mutationObserver.observe(target, {childList: true});

      return () => {
        intersectionObserver.disconnect();
        mutationObserver.disconnect();
      };
    });

    return () => {
      cleanupCallbacks.forEach((callback) => {
        if (callback) {
          callback();
        }
      });
    };
  }, [pathname]);

  useEffect(() => {
    if (!window.ym) return;
    const errorPage = document.getElementById('error');

    if (errorPage !== null) {
      ym_log.error404YM();
    }
    ym_log.setUserParamsOnYM();

    window.ym(YANDEX_METRIKA_ID, 'hit', document.location.href);
  }, [pathname]);

  useEffect(() => {
    window.xray?.addMiddleware((t, params, gaTrackingId) => {
      if (t === 'pk-err') {
        const error = params?.rlog_message?.err;

        const err = new Error();
        err.name = 'XRAY-PK-ERR';
        err.message = `Ошибка xray-pk-err - ${error?.url}`;
      }

      return {t, params, gaTrackingId};
    });
  }, []);

  useTracePathname();

  return <>{children}</>;
};
